<template>
	<div :class="{ expanded: expanded, disabled: disabled }" class="sidebar">
		<button
			class="sidebar-button"
			name="toggle-sidebar"
			:class="{ expanded: expanded }"
			:aria-label="$t('toggleSidebar')"
			:aria-expanded="expanded.toString()"
			:disabled="disabled"
			:tabindex="disabled ? -1 : 0"
			@click="toggleNav"
		>
			<svgicon name="arrow" class="home-icon" />
		</button>

		<div class="nav-menu" role="menu" aria-orientation="vertical">
			<button
				v-for="item in categories"
				:key="item.expenseCategory_ID"
				:ref="item.expenseCategory_ID"
				class="menu-btn"
				:class="{
					expanded: expanded,
					available: isCategoryEnabled(item),
					completed:
						isCategoryBudgeted(item) || item.isCompletedForCurrentStudent,
					disabled: isCategoryDisabled(item)
				}"
				:aria-disabled="
					isCategoryDisabled(item) || item.isCompletedForCurrentStudent
				"
				:disabled="disabled"
				:tabindex="disabled ? -1 : 0"
				role="menuitem"
				:aria-label="getCategoryAriaLabel(item)"
				aria-roledescription="menu button"
				@click="selectCategory(item)"
				@mousedown.prevent
			>
				<svgicon
					:name="`expenseCategories/${item.assetName}`"
					aria-hidden="true"
				></svgicon>

				<div v-show="expanded" class="nav-menu-text" aria-hidden="true">
					<span class="title">{{ item.name }}</span>
				</div>

				<span v-if="expanded && isCategoryBudgeted(item)" class="budget">
					<svgicon name="check" class="icon--small"></svgicon>
					{{ item.allocatedBudget | currency }}
				</span>
				<span
					v-if="isTagVisible(item)"
					:class="{ 'tag--dot': !expanded }"
					class="tag tag--rounded tag--orange tag--sm"
				>
					{{ $t('new') }}
				</span>
			</button>
		</div>
	</div>
</template>

<script>
import '../icons/arrow';
import '../icons/expenseCategories';
import CategoryCheck from '@/mixins/CategoryCheck';
import UserData from '@/mixins/UserData';

export default {
	mixins: [CategoryCheck, UserData],
	props: {
		categories: {
			type: Array,
			default: () => {
				return [];
			}
		},
		disabled: {
			type: Boolean,
			default: false
		},
		activeCategory: {
			type: String,
			default: null
		}
	},
	data() {
		return {
			expanded: false
		};
	},
	watch: {
		activeCategory(value) {
			if (value) {
				setTimeout(() => {
					this.$refs[value][0].focus();
				});
			}
		}
	},
	methods: {
		getCategoryAriaLabel(item) {
			if (item.isCompletedForCurrentStudent) {
				return this.$t('categoryCompleted', { category: item.name });
			}

			return item.name;
		},
		toggleNav() {
			if (!this.disabled) {
				this.expanded = !this.expanded;
				if (this.expanded) {
					this.$announcer.polite(this.$t('expanded'));
				}
			}
		},

		selectCategory(item) {
			if (this.isCategoryEnabled(item)) {
				this.$emit('categorySelected', item);
			}
		},
		isTagVisible(item) {
			if (
				this.$store.getters.isInBudgetingPhase &&
				this.userData.studentPreviousLifeStageId
			) {
				return !this.isCategoryBudgeted(item) && this.isCategoryEnabled(item);
			}
			return false;
		}
	}
};
</script>

<style lang="scss" scoped>
.sidebar {
	display: flex;
	flex-direction: column;
	background-color: $container-bgcolor;
	border: 1px solid $border-color;
	box-shadow: 0 15px 30px 0 rgba(0, 76, 208, 0.24);
	height: 100%;
	max-height: 100%;

	&.disabled {
		.sidebar-button {
			@media (any-hover: hover) {
				&:hover {
					cursor: default;
					background-color: $primary-state;
				}
			}
		}
		.nav-menu {
			.menu-btn {
				&.available {
					@media (any-hover: hover) {
						&:hover {
							cursor: default;
							color: $primary-state;
							.svg-icon {
								fill: $primary-state;
							}
						}
					}
				}
			}
		}
	}

	.sidebar-button {
		height: 38px;
		width: 100%;
		background-color: $primary-state;
		color: $border-color;
		flex-shrink: 0;
		border: 0;
		margin: auto;

		@media (any-hover: hover) {
			&:hover {
				cursor: pointer;
				background-color: $hover-state;
			}
		}

		&:active {
			background-color: $tapped-state;
		}

		&.expanded {
			display: block;
			text-align: left;
			padding-left: 28px;

			.home-icon {
				transform: rotate(180deg);
			}
		}

		.home-icon {
			width: 24px;
			height: auto;
		}
	}

	&.expanded {
		min-width: 390px;

		@media (min-width: $breakpoint-mid) {
			min-width: 420px;
		}
	}

	.nav-menu {
		display: flex;
		flex-direction: column;
		height: 100%;
		overflow-y: auto;
		margin: 0;
		padding: 0 16px;

		.menu-btn {
			flex-shrink: 0;
			height: 56px;
			min-width: 48px;
			display: flex;
			padding: 16px 4px;
			color: $sidebar-li-a;
			font-size: 14px;
			align-items: center;
			justify-content: center;
			border: 0;
			background-color: transparent;

			&.disabled {
				color: $icon-disabled;
				svg {
					fill: $icon-disabled;
				}
			}
			&.available {
				color: $icon-available;
				svg {
					fill: $icon-available;
				}
			}
			&.completed {
				color: $icon-completed;
				svg {
					fill: $icon-completed;
				}
			}

			&.available {
				@media (any-hover: hover) {
					&:hover {
						cursor: pointer;
						color: $hover-state;
						.svg-icon {
							fill: $hover-state;
						}
					}
				}

				&:active {
					color: $tapped-state;
					.svg-icon {
						fill: $tapped-state;
					}
				}
			}

			&:not(:last-child) {
				border-bottom: 1px solid $sidebar-border-bottom;
			}

			&.expanded {
				margin: 0px 16px;
				padding-left: 13px;
			}

			@media (min-width: $breakpoint-mid) {
				font-size: 16px;
			}

			.svg-icon {
				width: 20px;
			}

			.nav-menu-text {
				flex-grow: 1;
				display: inline-block;
				margin: 0px 16px;
				text-align: left;
			}
		}
	}
}
</style>
